import { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, FragmentType, ValidationActionType } from 'models/enum';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import { areAllStepsComplete, canSelectStep, getMaxSelectableStep } from 'components/features/chart/special-charts/shared/stepNavigation';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import {
  FIELD_CONFIGS,
  FIELD_CONFIG_MAP,
  FOCUSED_HISTORY_DETAILS_MAP,
  FormField,
  OBTriageFieldConfig,
  STEP_CONFIGS,
  STEP_FIELD_NAMES,
  SectionTitle,
  getFocusedHistoryParentField
} from './constants';
import OBTriageView, { OBTriageViewProps } from './OBTriageView';

export interface OBTriageProps extends ChartComponentProps {
  enableMultiStepsAuthoring?: (isSupportMultiStepsAuthoring: boolean) => void;
  navigateToSavedPatientCharting: VoidFunction;
}

const LAST_STEP_INDEX = STEP_CONFIGS.length - 1;
// Pre-populate values for fields that should be pre-filled for validation purposes
const DEFAULT_FIELD_VALUES: Partial<Record<FormField, string>> = {};

const LMP_UNIT_FIELD_MAP: Partial<Record<FormField, string>> = {
  [FormField.LMP_DAYS]: 'days',
  [FormField.LMP_WEEKS]: 'weeks'
};

// Unique saved-chart titles to avoid deduplication in cookSectionRecords (EditChart.tsx)
const SAVED_TITLE_MAP: Partial<Record<FormField, string>> = {
  [FormField.MEMBRANES_STATUS]: 'Status',
  [FormField.NURSE_SIGNATURE_TIME]: 'Nurse Time',
  [FormField.PROVIDER_SIGNATURE_TIME]: 'Provider Time'
};

const EDB_UNIT_FIELD_MAP: Partial<Record<FormField, string>> = {
  [FormField.EDB_DAYS_FROM_NOW]: 'days from now',
  [FormField.EDB_WEEKS_FROM_NOW]: 'weeks from now'
};

const shouldIncludeFieldInFragment = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField): boolean => {
  const selectedLmpUnit = String(formFieldMap.get(FormField.LMP_HOW_LONG_AGO_UNIT)?.value || '').toLowerCase();
  const selectedEdbUnit = String(formFieldMap.get(FormField.EDB_FROM_NOW_UNIT)?.value || '').toLowerCase();

  const lmpUnit = LMP_UNIT_FIELD_MAP[fieldName];
  if (lmpUnit && selectedLmpUnit !== lmpUnit) return false;

  const edbUnit = EDB_UNIT_FIELD_MAP[fieldName];
  if (edbUnit && selectedEdbUnit !== edbUnit) return false;

  return true;
};

const buildFormField = (fieldConfig: OBTriageFieldConfig): ChartMetaFormField => {
  const formField = chartHelper.createFormField({ name: fieldConfig.name, type: fieldConfig.type, label: fieldConfig.label });

  return {
    ...formField,
    isRequired: Boolean(fieldConfig.isRequired),
    value: DEFAULT_FIELD_VALUES[fieldConfig.name] ?? ''
  };
};

export const buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
  const dataMap = new Map();

  FIELD_CONFIGS.forEach((fieldConfig) => {
    dataMap.set(fieldConfig.name, buildFormField(fieldConfig));
  });

  return dataMap;
};

const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;

  const { value } = formField;

  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return true;

  const strVal = String(value ?? '').trim();
  return strVal.length > 0;
};

const flattenChartRecords = (records: any[] = []): any[] =>
  records.flatMap((record) => {
    const childRecords = Array.isArray(record?.records) ? flattenChartRecords(record.records) : [];
    return record?.formField ? [record, ...childRecords] : childRecords;
  });

const buildHydratedField = (field: ChartMetaFormField, record: any): ChartMetaFormField => {
  const hydratedValue = record?.value ?? record?.content ?? '';
  const hydratedField: ChartMetaFormField = {
    ...field,
    value: hydratedValue
  };

  if (!Array.isArray(field.chartContent) || field.chartContent.length === 0) {
    return hydratedField;
  }

  const selectedContent = field.chartContent.find((item) => item.id === record?.contentId || item.value === hydratedValue || item.label === record?.content);
  if (!selectedContent) {
    return hydratedField;
  }

  return {
    ...hydratedField,
    value: selectedContent.value,
    chartContent: field.chartContent.map((item) => ({
      ...item,
      selected: item.id === selectedContent.id
    }))
  };
};

export const buildAuthoredFormFields = (formFieldMap: Map<string, ChartMetaFormField>, fragments: ChartFragment[]): Map<string, ChartMetaFormField> => {
  const hydratedFieldMap = new Map(formFieldMap);

  [...fragments]
    .filter((fragment) => fragment?.chartData?.records?.length > 0)
    .sort((left, right) => Number(left.fragmentType !== FragmentType.CHARTING) - Number(right.fragmentType !== FragmentType.CHARTING))
    .forEach((fragment) => {
      flattenChartRecords(fragment.chartData.records).forEach((record) => {
        if (!record?.formField || !FIELD_CONFIG_MAP.has(record.formField)) return;

        const currentField = hydratedFieldMap.get(record.formField);
        if (!currentField) return;

        hydratedFieldMap.set(record.formField, buildHydratedField(currentField, record));
      });
    });

  return hydratedFieldMap;
};

const buildRequiredSanitizedFieldMap = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> => {
  const sanitizedMap = new Map<string, ChartMetaFormField>();

  formFieldMap.forEach((field, name) => {
    sanitizedMap.set(name, {
      ...field,
      isRequired: false,
      errors: (field.errors || []).filter((error) => error?.type !== ValidationActionType.REQUIRED)
    });
  });

  return sanitizedMap;
};

const OBTriage = (props: OBTriageProps) => {
  const {
    chartingTime,
    displayAuthoringData,
    enableDisplayRecordsButton,
    enableMultiStepsAuthoring,
    formFieldMap,
    formSubmittedCount,
    initState,
    navigateToSavedPatientCharting,
    resetFormSubmitted,
    saveChart
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const safeFormFieldMap = formFieldMap || buildDefaultFormFields();
  const safeFormFieldMapWithoutRequiredErrors = useMemo(() => buildRequiredSanitizedFieldMap(safeFormFieldMap), [safeFormFieldMap]);

  useEffect(() => {
    initState(buildDefaultFormFields());
    enableMultiStepsAuthoring?.(true);
  }, [enableMultiStepsAuthoring, initState]);

  useEffect(() => {
    if (!props.loadChartData || props.selectedNavId !== 'SPECIAL_CHARTS_033') return;

    void props
      .loadChartData([FragmentType.CHARTING, FragmentType.AUTHORED], [props.selectedNavId])
      .then(({ data }) => {
        if (!data?.length) return;

        initState(buildAuthoredFormFields(buildDefaultFormFields(), data));
      })
      .catch(() => ({ data: [] }));
  }, [initState, props.loadChartData, props.selectedNavId]);

  const isStepComplete = useCallback(
    (stepIndex: number) => {
      const selectedLmpUnit = String(safeFormFieldMap.get(FormField.LMP_HOW_LONG_AGO_UNIT)?.value || '').toLowerCase();
      const selectedEdbUnit = String(safeFormFieldMap.get(FormField.EDB_FROM_NOW_UNIT)?.value || '').toLowerCase();

      return STEP_FIELD_NAMES[stepIndex].every((fieldName) => {
        const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
        if (!fieldConfig?.isRequired) return true;

        const lmpUnit = LMP_UNIT_FIELD_MAP[fieldName];
        if (lmpUnit && selectedLmpUnit !== lmpUnit) return true;

        const edbUnit = EDB_UNIT_FIELD_MAP[fieldName];
        if (edbUnit && selectedEdbUnit !== edbUnit) return true;

        const focusedParentField = getFocusedHistoryParentField(fieldName);
        if (focusedParentField && !hasFieldValue(safeFormFieldMap.get(focusedParentField))) return true;

        return hasFieldValue(safeFormFieldMap.get(fieldName));
      });
    },
    [safeFormFieldMap]
  );

  const stepAccess = { stepCount: STEP_CONFIGS.length, furthestStep, isStepComplete };
  // The submit gate covers the whole form: reaching the last step says nothing about the earlier ones.
  const isFinalStepReady = areAllStepsComplete(stepAccess);
  // A step is visually completed only after the user progresses past it via Continue, and only while
  // its data still stands - clearing a step withdraws its checkmark.
  const completedSteps = STEP_CONFIGS.map((_, index) => index < furthestStep && isStepComplete(index));
  const maxSelectableStep = getMaxSelectableStep(stepAccess);

  const buildFragment = useCallback((): ChartFragment => {
    const { buildPlainRecord } = chartService;

    const buildOBTriageRecord = (fieldName: FormField) => {
      const formField = safeFormFieldMap.get(fieldName);
      if (!formField) return null;

      const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
      const fieldValue = String(formField.value ?? '').trim();

      // Focused history DETAIL fields are consumed by their parent checkbox record
      if (getFocusedHistoryParentField(fieldName)) return null;

      if (!fieldValue) return null;

      // CHECK_BOX: use chartContent label as title, include detail value as content
      if (formField.inputType === FormFieldInputType.CHECK_BOX) {
        const selectedItem = formField.chartContent?.find((item) => item.selected);
        if (!selectedItem) return null;
        const detailFieldName = FOCUSED_HISTORY_DETAILS_MAP[fieldName];
        const detailValue = detailFieldName ? String(safeFormFieldMap.get(detailFieldName)?.value ?? '').trim() : '';
        return buildPlainRecord({
          formField: formField.name,
          title: selectedItem.label || '',
          content: detailValue || true,
          value: selectedItem.value,
          contentId: selectedItem.id
        });
      }

      // RADIO_CHOICE / DROPDOWN: sync selection from value if needed
      if (formField.inputType === FormFieldInputType.RADIO_CHOICE || formField.inputType === FormFieldInputType.DROPDOWN) {
        let selectedContent = formField.chartContent?.find((item) => !!item.value && item.selected);
        if (!selectedContent) {
          selectedContent = formField.chartContent?.find((item) => item.value === fieldValue);
        }
        if (!selectedContent) return null;
        return buildPlainRecord({
          formField: formField.name,
          title: SAVED_TITLE_MAP[fieldName] || fieldConfig?.label || formField.label || '',
          content: selectedContent.label,
          value: selectedContent.value,
          contentId: selectedContent.id
        });
      }

      // TEXT_BOX / TIME / TEXT_AREA / DATE: use field config label as title
      const title = SAVED_TITLE_MAP[fieldName] || fieldConfig?.label || formField.label || '';
      return buildPlainRecord({
        formField: formField.name,
        title,
        content: formField.value,
        value: formField.value
      });
    };

    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.OB_TRIAGE,
      records: STEP_CONFIGS.flatMap((stepConfig) =>
        stepConfig.sections.map((section) => ({
          sectionTitle: section.title,
          records: section.rows.flatMap((row) =>
            row.filter((fieldName) => shouldIncludeFieldInFragment(safeFormFieldMap, fieldName)).map((fieldName) => buildOBTriageRecord(fieldName))
          )
        }))
      )
    };

    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  }, [chartingTime, safeFormFieldMap]);

  const handleSaveClick = useCallback(() => {
    saveChart([buildFragment()], {
      formFieldMap: safeFormFieldMapWithoutRequiredErrors,
      afterSave: () => {
        if (!props.isAuthor) {
          navigateToSavedPatientCharting();
        }
      }
    });
  }, [buildFragment, navigateToSavedPatientCharting, props.isAuthor, safeFormFieldMapWithoutRequiredErrors, saveChart]);

  const handleSelectStep = useCallback(
    (stepIndex: number) => {
      if (props.isAuthor && !canSelectStep(stepIndex, { stepCount: STEP_CONFIGS.length, furthestStep, isStepComplete })) return;
      setActiveStep(stepIndex);
      resetFormSubmitted?.();
    },
    [furthestStep, isStepComplete, props.isAuthor, resetFormSubmitted]
  );

  const handleBackStep = useCallback(() => {
    if (activeStep === 0) return;
    setActiveStep(activeStep - 1);
    resetFormSubmitted?.();
  }, [activeStep, resetFormSubmitted]);

  const handleContinueStep = useCallback(() => {
    if (activeStep === LAST_STEP_INDEX) {
      if (!isFinalStepReady) return;
      handleSaveClick();
      return;
    }

    const nextStep = activeStep + 1;
    setFurthestStep((previousStep) => Math.max(previousStep, nextStep));
    setActiveStep(nextStep);
    resetFormSubmitted?.();
  }, [activeStep, handleSaveClick, isFinalStepReady, resetFormSubmitted]);

  const viewProps: OBTriageViewProps = {
    activeStep,
    canContinue: activeStep === LAST_STEP_INDEX ? isFinalStepReady : props.isAuthor ? isStepComplete(activeStep) : true,
    completedSteps,
    displayAuthoringData,
    enableDisplayRecordsButton: Boolean(enableDisplayRecordsButton),
    formFieldMap: safeFormFieldMapWithoutRequiredErrors,
    formSubmittedCount,
    maxSelectableStep,
    isAuthor: Boolean(props.isAuthor),
    onBackStep: handleBackStep,
    onContinueStep: handleContinueStep,
    onSelectStep: handleSelectStep
  };

  return <OBTriageView {...viewProps} />;
};

export { OBTriage as BaseOBTriage };
export default compose(withSavedPatientChartsPage, withChartLogic)(OBTriage);
